import { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, Input, Table, message } from "antd";
import { CloseOutlined, SaveFilled, SearchOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import LocationAPI from "../../api/LocationAPI";

import "./location-maintenance.styles.scss";
import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";

const locationFormInitialValues = {
  id: "",
  building: "",
  description: "",
};
const locationSearchFormInitialValues = {
  building: "",
};

const LocationMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [locationsData, setLocationsData] = useState({
    locations: [],
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isSelected, setIsSelected] = useState(false);

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await LocationAPI.getLocations(token);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setLocationsData(response.data);
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  }, [token, dispatch, messageApi]);

  const loadLocationList = async (activePage = 1, activePageSize = 10) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await LocationAPI.getLocations(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setCurrentPage(activePage);
      setLocationsData(response.data);
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  };

  const loadSelectedLocation = async (id) => {
    try {
      const response = await LocationAPI.getLocation(token, id);
      const locationResponse = response.data;

      if (!locationResponse.success) {
        messageApi.open({ type: "error", message: locationResponse.message });
        return;
      }

      const { Id, Building, Description } = locationResponse.location;

      form.setFieldsValue({
        id: Id,
        building: Building,
        description: Description,
      });

      setIsSelected(true);
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      console.error("ERROR: ", err.response);

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const onFinishLocationForm = async (values) => {
    try {
      let response = null;

      if (isSelected) {
        response = await LocationAPI.updateLocation(token, values);
      } else {
        response = await LocationAPI.createLocation(token, values);
      }

      const createLocationResponse = response.data;

      messageApi.open({
        type: createLocationResponse.success ? "success" : "error",
        content: createLocationResponse.message,
      });

      if (createLocationResponse.success) {
        form.resetFields();
        loadLocationList();
        setIsSelected(false);
      }
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;
        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const locationTableColumns = [
    { title: "Número de residencia", dataIndex: "Building" },
    { title: "Descripción", dataIndex: "Description" },
    {
      title: "Opción",
      render: (_, { Id }) => (
        <Button onClick={() => loadSelectedLocation(Id)}>Seleccionar</Button>
      ),
    },
  ];

  useEffect(() => {
    dispatch(setMenu("locationMaintenance"));
    loadInitialData();
  }, [dispatch, loadInitialData]);

  return (
    <div className="location-maintenance">
      {messageContextHolder}
      <div className="location-form-container">
        <Card title="Filtro">
          <Form
            initialValues={locationSearchFormInitialValues}
            form={searchForm}
            onFinish={() => loadLocationList()}
          >
            <Form.Item label="Número" name="building">
              <Input
                suffix={
                  <CloseOutlined
                    onClick={() => {
                      searchForm.setFieldValue("building", "");
                      loadLocationList();
                    }}
                  />
                }
              />
            </Form.Item>
            <Button htmlType="submit" type="primary" icon={<SearchOutlined />}>
              Buscar
            </Button>
          </Form>
        </Card>
        <Card title={`${isSelected ? "Actualizar" : "Registrar"} Residencia`}>
          <Form
            initialValues={locationFormInitialValues}
            form={form}
            onFinish={onFinishLocationForm}
            autoComplete="off"
          >
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Número de residencia" name="building" required>
              <Input required />
            </Form.Item>
            <Form.Item label="Descripción" name="description">
              <Input />
            </Form.Item>
            <div className="buttons-container">
              <Button type="primary" htmlType="submit" icon={<SaveFilled />}>
                Guardar
              </Button>
              <Button
                htmlType="button"
                onClick={() => {
                  form.resetFields();
                  form.submit();
                  setIsSelected(false);
                  setCurrentPage(1);
                }}
              >
                Limpiar
              </Button>
            </div>
          </Form>
        </Card>
      </div>
      <div className="location-list-container">
        <DataTotalDisplay
          currentCount={locationsData.locations.length}
          totalCount={locationsData.totalCount}
        />
        <Table
          columns={locationTableColumns}
          dataSource={locationsData.locations}
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: locationsData.totalCount,
          }}
          onChange={async (e) => {
            await loadLocationList(e.current, e.pageSize);
            setCurrentPage(e.current);
            setCurrentPageSize(e.pageSize);
          }}
          scroll={{ y: "400px" }}
        />
      </div>
    </div>
  );
};

export default LocationMaintenancePage;
